/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE file for details)
*/

import React, { useState, useEffect } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { Typography } from "@mui/material";
import titles from "./titles";

import Repetition from "./Repetition";

const NestedForm = ({ questions, titles, index, data, setData, indexIntervention }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedSub, setSelectedSub] = useState(0);
  const [methodError, setMethodError] = useState(false);
  const [typeOtherError, setTypeOtherError] = useState(false);
  const intervention = data[index].interventions[indexIntervention];
  const methodOptions = selectedIndex < questions.length ? questions[selectedIndex] : [];
  
  const isMethodOther =
    selectedSub === methodOptions.length - 1 &&
    methodOptions[methodOptions.length - 1].toLowerCase().includes("other");

  const isTypeOther =
    selectedIndex === questions.length ||
    titles[selectedIndex]?.label.toLowerCase().includes("other");

  const selectedMethodIndex = methodOptions.findIndex(
    (m) => m === intervention.interventionMethod
  );

  const methodSelectValue =
    selectedMethodIndex !== -1 ? selectedMethodIndex : '';

  const selectedTypeIndex = titles.findIndex(
    (t) => t.label === intervention.interventionType
  );

  const typeSelectValue = 
    selectedTypeIndex !== -1 ? selectedTypeIndex : '';


      

  useEffect(() => {
    const intervention = data[index].interventions[indexIntervention];
    const indexTitle = titles.findIndex(t => t.label === intervention.interventionType);
    if (indexTitle !== -1) {
      setSelectedIndex(indexTitle);
    }
    if (indexTitle < questions.length) {
      const methodIndex = 
      indexTitle >= 0 && indexTitle < questions.length
        ? questions[indexTitle].indexOf(intervention.interventionMethod)
        : -1;
          if (methodIndex !== -1) {
        setSelectedSub(methodIndex);
      }
    }
  }, [data, index, indexIntervention, questions, titles]);

  // s'il y a un type sélectionné alors il faut une method et il faut remplir les textes des other
  const validateMethod = () => {
    const typeIsOther =
      intervention.interventionType && 
      intervention.interventionType.toLowerCase().includes("other");

    const methodIsOther =
      intervention.interventionMethod && intervention.interventionMethod.toLowerCase().includes("other");

    const hasType = intervention.interventionType && intervention.interventionType !== "";
    const hasTypeOther = !typeIsOther || (
      intervention.interventionTypeOther && 
      intervention.interventionTypeOther.trim() !== ""
    );

    const hasMethod =
      intervention.interventionMethod && (methodIsOther
        ? intervention.interventionMethodOther && intervention.interventionMethodOther.trim() !== ""
        : true);

    setTypeOtherError(typeIsOther && !hasTypeOther);
    setMethodError(hasType && hasTypeOther && !hasMethod);
  }
  // mise à jour du tableau data
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newData = [...data];
    const newInterventions = [...newData[index].interventions];

    if (name === "interventionMethod") {
      newInterventions[indexIntervention].interventionMethodOther = value;
    } else if (name === "interventionType") {
      newInterventions[indexIntervention].interventionTypeOther = value;
    } else {
      newInterventions[indexIntervention][name] = value;
    }

    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);

    validateMethod();
  };

  const handleTypeChange = (e) => {
    const idx = Number(e.target.value);
    if (isNaN(idx)) return;
    setSelectedIndex(idx); 
    setSelectedSub(0);
    const newData = [...data]; 
    const newInterventions = [...newData[index].interventions];
    newInterventions[indexIntervention] = {
      ...newInterventions[indexIntervention],
      interventionType: titles[idx].label,
      interventionTypeOther: titles[idx].label.toLowerCase().includes("other") ? "" : undefined,
      interventionMethod: "",
      interventionMethodOther: "",
    };
    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);

    validateMethod();
  };

  const handleMethodChange = (e) => {
    const idx = Number(e.target.value);
    setSelectedSub(idx);// met a jour l'état local (index de la méthode sélectionné)
    const newData = [...data]; //copie tableau data 
    const newInterventions = [...newData[index].interventions];  // copie tableau des interventions
    const selectedValue = methodOptions[idx];

    newInterventions[indexIntervention] = { // mise a jour d'une seule intervention
      ...newInterventions[indexIntervention],
      interventionMethod: selectedValue,
      // si Other, on garde une clé séparé pour la valeur saisie
      interventionMethodOther: selectedValue.toLowerCase().includes("other") ? "" : undefined
    };
    newData[index] = { ...newData[index], interventions: newInterventions };
    setData(newData);

    validateMethod();
  };

  return (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.83rem", lineHeight: 1.6 }}>
        An <strong>intervention</strong> is any hands-on action performed on an animal — injection, surgery,
        sampling, behavioural test, imaging, etc. Select the category that best matches, then choose the
        specific method. Use <em>Other</em> if none of the listed options apply.
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="interventionTypeLabel">Intervention type</InputLabel>
        <Select
          id="interventionType"
          name="interventionType"
          label="Intervention type"
          value={typeSelectValue}
          onChange={handleTypeChange}
          error={intervention._fieldError && !intervention.interventionType}
        >
          <MenuItem value="">
              <em></em>
          </MenuItem>
          {titles.map((title, idx) => (
          <MenuItem key={idx} value={idx}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {title.icon && (
              <img
                src={title.icon}
                alt={title.label}
                style={{ width: 24, height: 24 }}
              />
            )}
            <span style={{ fontSize: "1.1rem", fontWeight: 500 }}>
              {title.label}
            </span>
          </div>
        </MenuItem>
))}

        </Select>
      </FormControl>

      {selectedIndex < questions.length && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="methodLabel">Specific method or technique</InputLabel>
          <Select
            id="method"
            name="interventionMethod"
            label="Specific method or technique"
            value={methodSelectValue}
            onChange={handleMethodChange}
            error={intervention._fieldError && !intervention.interventionMethod}
          >
            <MenuItem value="">
              <em></em>
            </MenuItem>
            {methodOptions.map((subQ, subIdx) => (
              <MenuItem key={subIdx} value={subIdx}>
                {subQ}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {isMethodOther &&
        selectedSub === methodOptions.length - 1 && (
          <TextField
            id="interventionMethodOther"
            name="interventionMethod"
            label="Specify method"
            variant="outlined"
            fullWidth
            sx={{ mb: 2 }}
            value={intervention.interventionMethodOther || ""}
            onChange={handleChange}
            error={intervention._fieldError && isMethodOther && (!intervention.interventionMethodOther || intervention.interventionMethodOther.trim() === "")}
            helperText={
              intervention._fieldError && isMethodOther && (!intervention.interventionMethodOther || intervention.interventionMethodOther.trim() === "")
                ? "Please specify the method"
                : ""
            }
          />
        )}

      {isTypeOther && (
        <TextField
          id="interventionTypeOther"
          name="interventionType"
          label="Specify type"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          value={intervention.interventionTypeOther || ""}
          onChange={handleChange}
          error={intervention._fieldError && isTypeOther && !intervention.interventionTypeOther}
          helperText={intervention._fieldError ? "Please specify the type of intervention" : "Describe the intervention type if it does not match any of the listed categories."}
        />
      )}

      <Repetition index={index} data={data} setData={setData} indexIntervention={indexIntervention} />
    </div>
  );
};

export default function TypeIntervention({ index, data, setData, indexIntervention }) {
  const subCategories = [
    [
      "By oral gavage",
      "By tail intravenous injection",
      "By hydrodynamic tail vein injection",
      "By retro-orbital injection",
      "By intraperitoneal injection",
      "By subcutaneous injection",
      "By intramuscular injection",
      "In the drinking water",
      "In the food",
      "By means of a micropipette",
      "Other"
    ],
    [
      "Brain stereotaxic injection",
      "Brain viral injection (no stereotaxic)",
      "Placement of head implants",
      "In utero surgery",
      "Transient renal ischemia",
      "Telemetric implants for blood pressure",
      "Osmotic mini pumps implants",
      "Other surgery"
    ],
    [
      "Pole test",
      "Running Wheel Test",
      "Open Field",
      "CatWalk",
      "Rotarod",
      "Y-maze",
      "T-maze",
      "Novel object recognition",
      "Grid walk",
      "Other behavioral test"
    ],
    [
      "Glucose tolerance test",
      "Labmaster",
      "Metabolic cage",
      "Insulin tolerance test",
      "Pyruvate tolerance test",
      "Measuring muscle strength",
      "Body weight measurement",
      "Other physiological test"
    ],
    [
      "Electrical microstimulation",
      "Optogenetic stimulation",
      "Fiber photometry recording",
      "Intrinsic signal and wide-field fluorescence imaging",
      "In vivo neuroelectrophysiology",
      "Electromyography",
      "Electrocardiogram measurements",
      "Other electrophysiological test"
    ],
    [
      "Total body Irradiation",
      "X-ray imaging",
      "Flash RT",
      "Other irradiation (please specify)"
    ],
    [
      "Subcutaneous transplantation of tumor cells",
      "Mammary fat pad transplantation of tumor cells",
      "Liver cancer induction",
      "Ovarian cancer induction",
      "Orthotopic lung tumor implantation",
      "Other tumor induction method"
    ],
    [
      "Blood sampling",
      "Biopsy",
      "CSF collection",
      "Feces collection",
      "Urine collection",
      "Other collection of samples"
    ],
    [
      "Computed Tomography",
      "Bioluminescence imaging",
      "Magnetic Resonance Imaging",
      "Positron emission tomography",
      "Micro-tomography",
      "Other imaging procedure"
    ],
    [
      "Ear clipping",
      "Shaving",
      "Pen marking",
      "Phalangeal section",
      "Other marking procedure"
    ],
    [
      "Euthanasia",
      "Perfusion, fixation and euthanasia",
      "Blood collection and euthanasia",
      "Rehoming",
      "Use for breeding",
      "Re-use in another experiment",
      "Other terminal intervention"
    ]
  ];

  return (
    <div className="p-6">
      <NestedForm questions={subCategories} titles={titles} index = {index} data={data} setData={setData} indexIntervention={indexIntervention}/>
    </div>
  );
}
